/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';

//----------------------------------------------------------
// @CUSTOM: Added imports for additional config from
// extended definition
//----------------------------------------------------------
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
//----------------------------------------------------------

const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = props => {    // @CUSTOM: Added extended definition props
    const {
        config, // @CUSTOM: Get config from extended definition
        slots,   // @CUSTOM: Get slots from extended definition
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer
        // @CUSTOM: Removed 'Divider' as it is not used here
    } = props;

    //----------------------------------------------------------
    // @CUSTOM: Pulling in config from extended definition
    //----------------------------------------------------------
    const {
        hideCart,
        hideAccount,
        hideWishlist
    } = config;
    //----------------------------------------------------------

    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>

                    {/* @CUSTOM: TSX below has been reorganized for the theme */}
                    <div className='ms-header__topbar-left'>
                        {props.navIcon}
                        {_renderReactFragment(slots.secondaryMenuBar)}
                    </div>
                    <div className='ms-header__topbar-center'>
                        {props.logo}
                    </div>
                    <div className='ms-header__topbar-right'>
                        {_renderReactFragment(props.search)}
                        {!hideAccount && _renderDesktopAccountBlock(props)}
                        {!hideWishlist && props.wishListIconDesktop}
                        {!hideCart && props.cartIcon}
                        {props.preferredStore}
                        {_renderReactFragment(props.siteOptions)}
                    </div>
                    {/*-------------------------------------------------------*/}

                </Node>
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        {props.MobileMenuHeader}

                        {/* @CUSTOM: TSX below has been reorganized for the theme */}
                        {!hideAccount && props.signInLink}
                        {_renderReactFragment(props.menuBar)}
                        {_renderReactFragment(slots.secondaryMenuBar)}
                        <Node {...MobileMenuLinksContainer}>
                            {!hideAccount && props.accountLinks ? props.accountLinks.map(link => link) : false}
                            {props.siteOptions}
                            {!hideWishlist && props.wishListIconMobile}
                            {!hideAccount && props.signOutLink}
                        </Node>
                        {/*-------------------------------------------------------*/}

                    </Node>
                </Node>

                {/* @CUSTOM: TSX below has been reorganized for the theme */}
                <div className='ms-header__bottombar'>
                    {_renderReactFragment(props.menuBar)}
                </div>
                {/*-------------------------------------------------------*/}

            </Node>
        </Module>
    );
};

function _renderDesktopAccountBlock(props: IHeaderViewProps): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map(link => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return <Node {...AccountInfoDropdownParentContainer}>{signInLink}</Node>;
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

export default headerView;
